import { createContext, useCallback, useContext, useEffect, useState } from "react";
import AxiosClient, { API_BASE_URL } from "../assets/AxiosClient";

const TOKEN_KEY = "OCM_ACCESS_TOKEN";
const USER_KEY = "OCM_USER";
const TENANT_KEY = "OCM_TENANT";
const StateContext = createContext(null);

export const ContextProvider = ({ children }) => {
    const [user, _setUser] = useState(null);
    const [token, _setToken] = useState(() => {
        const currentToken = localStorage.getItem(TOKEN_KEY);
        const legacyToken = localStorage.getItem("ADMIN_ACCESS_TOKEN") || localStorage.getItem("Admin_ACCESS_TOKEN");
        const restoredToken = currentToken || legacyToken;
        if (restoredToken && !currentToken) localStorage.setItem(TOKEN_KEY, restoredToken);
        localStorage.removeItem("ADMIN_ACCESS_TOKEN");
        localStorage.removeItem("Admin_ACCESS_TOKEN");
        localStorage.removeItem("ADMINUSER");
        return restoredToken;
    });
    const [tenant, _setTenant] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const [bootstrapError, setBootstrapError] = useState(null);
    const [data, setData] = useState([]);
    const [notifications, SetNotifications] = useState([]);
    const [LastSelectedConference, setLastSelectedConference] = useState(null);

    const clearAuth = useCallback(() => {
        _setUser(null);
        _setToken(null);
        _setTenant(null);
        setBootstrapError(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(TENANT_KEY);
    }, []);

    const setToken = (value) => {
        _setToken(value || null);
        if (value) localStorage.setItem(TOKEN_KEY, value);
        else localStorage.removeItem(TOKEN_KEY);
    };

    const setUser = (value) => {
        _setUser(value || null);
        if (value) localStorage.setItem(USER_KEY, JSON.stringify(value));
        else localStorage.removeItem(USER_KEY);
    };

    const setTenant = (value) => {
        _setTenant(value || null);
        if (value) localStorage.setItem(TENANT_KEY, JSON.stringify(value));
        else localStorage.removeItem(TENANT_KEY);
    };

    useEffect(() => {
        const handleUnauthorized = () => clearAuth();
        window.addEventListener("ocm:unauthorized", handleUnauthorized);
        return () => window.removeEventListener("ocm:unauthorized", handleUnauthorized);
    }, [clearAuth]);

    useEffect(() => {
        let active = true;
        if (!token) {
            setIsInitializing(false);
            return () => { active = false; };
        }

        setIsInitializing(true);
        AxiosClient.get("/api/user")
            .then(({ data: responseUser }) => {
                if (!active) return;
                const authenticatedUser = responseUser?.user || responseUser;
                let cachedTenant = null;
                try {
                    cachedTenant = JSON.parse(localStorage.getItem(TENANT_KEY) || "null");
                } catch {
                    localStorage.removeItem(TENANT_KEY);
                }
                const responseTenant = responseUser?.tenant || authenticatedUser?.tenant || null;
                const cachedTenantMatchesUser = cachedTenant?.id && authenticatedUser?.tenant_id
                    && String(cachedTenant.id) === String(authenticatedUser.tenant_id);
                const authenticatedTenant = responseTenant || (cachedTenantMatchesUser ? cachedTenant : null);
                setUser(authenticatedUser);
                setTenant(authenticatedTenant);
                setBootstrapError(null);
            })
            .catch((error) => {
                if (!active || error.response?.status === 401) return;
                setBootstrapError(error.response?.data?.message || "Unable to verify your tenant access. Please try again.");
            })
            .finally(() => {
                if (active) setIsInitializing(false);
            });

        return () => { active = false; };
    }, [token]);

    const SetNotification = (message, type) => {
        const id = Date.now() + Math.random();
        SetNotifications((current) => [...current, { message, type, id }]);
        setTimeout(() => SetNotifications((current) => current.filter((item) => item.id !== id)), 4000);
    };

    const tenantIdMatchesUser = tenant?.id && user?.tenant_id != null
        ? String(tenant.id) === String(user.tenant_id)
        : Boolean(tenant?.id && user?.tenant?.id && String(tenant.id) === String(user.tenant.id));
    const value = {
        user,
        token,
        tenant,
        setUser,
        setToken,
        setTenant,
        isAuthenticated: Boolean(token && user),
        isInitializing,
        isTenantResolved: Boolean(tenantIdMatchesUser),
        bootstrapError,
        logout: clearAuth,
        data,
        setData,
        API: API_BASE_URL,
        LastSelectedConference,
        notifications,
        SetNotification,
        setLastSelectedConference,
    };

    return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
};

export const useStateContext = () => useContext(StateContext);
